import { ref, watchEffect } from "@vue/composition-api";
import config from "@/config";
import * as amplitude from "@amplitude/unified";
import { auth, isLoggedIn } from "@micad/vue-components";
import { initApp } from "@/modules/auth";
import { getUserSession } from "@/data/repos/userInfo";
import { getKV } from "@/modules/browserLocalStorage";
import { CLIENT_COOKIE_NAME } from "@/interfaces/common.interfaces";
import { getClientConfigValue } from "@/data/repos/clientConfig";
import router, { anonymousRoutes } from "@/router";

// Parses "CustomerType: Type A, AnotherKey: Another value" into
// { CustomerType: "Type A", AnotherKey: "Another value" }.
// Entries without a key or a value are skipped.
const parseProperties = (text: string): Record<string, string> => {
  const properties: Record<string, string> = {};

  for (const entry of text.split(",")) {
    const separator = entry.indexOf(":");
    if (separator < 0) continue;

    const key = entry.slice(0, separator).trim();
    const value = entry.slice(separator + 1).trim();
    if (key && value) properties[key] = value;
  }

  return properties;
};

// Extra user properties configured per client in the "Amplitude" config entry,
// or undefined if the request failed.
const getClientProperties = async (): Promise<
  Record<string, string> | undefined
> => {
  const resp = await getClientConfigValue("Amplitude");
  if (!resp.ok) return undefined;

  const value = resp.payload?.value;
  return typeof value === "string" ? parseProperties(value) : {};
};

// The config properties request of each client.
const clientProperties = new Map<
  string,
  Promise<Record<string, string> | undefined>
>();

// A failed request is retried after this long, not on every navigation.
const CLIENT_PROPERTIES_RETRY_DELAY = 5 * 60 * 1000;

const loadClientProperties = (clientKey: string) => {
  let request = clientProperties.get(clientKey);
  if (!request) {
    request = getClientProperties();
    clientProperties.set(clientKey, request);
    request.then((properties) => {
      if (!properties) {
        setTimeout(
          () => clientProperties.delete(clientKey),
          CLIENT_PROPERTIES_RETRY_DELAY
        );
      }
    });
  }
  return request;
};

// The properties sent last, so identical ones are not sent again.
let sentProperties = "";

// Sets user properties without sending an event; undefined values are skipped.
const setUserProperties = (
  properties: Record<string, string | number | boolean | undefined>
) => {
  const json = JSON.stringify(properties);
  if (json === sentProperties) return;
  sentProperties = json;

  const identify = new amplitude.Identify();

  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined) identify.set(key, value);
  }

  amplitude.identify(identify);
};

// Expires the SDK's identity cookie, "AMP_" plus the first 10 characters of
// the API key (getCookieName in @amplitude/analytics-core; check it when
// upgrading the SDK). The SDK sets it with path=/ on the widest domain it can
// write, which isn't known here, so it's expired on each of this host's
// domains.
const clearIdentityCookie = (apiKey: string) => {
  const expired = `AMP_${apiKey.substring(0, 10)}=; expires=${new Date(
    0
  ).toUTCString()}; path=/`;
  document.cookie = expired;

  const parts = window.location.hostname.split(".");
  for (let i = 0; i < parts.length - 1; i++) {
    document.cookie = `${expired}; domain=.${parts.slice(i).join(".")}`;
  }
};

// All Micad apps share one Amplitude API key, and the SDK keeps the user and
// device IDs in a cookie shared across the apps' subdomains. So the device ID
// is reset only on an explicit logout; without a login only the user ID is
// cleared (see trackIdentity).
export const logout = async () => {
  if (!(await initApp()) || !config.AMPLITUDE_API_KEY) return;

  // If the SDK hasn't started yet, it queues the reset and applies it then,
  // which may be after the page is left. Clearing the cookie as well removes
  // the identity before the redirect, for this app and the others.
  amplitude.reset();
  clearIdentityCookie(config.AMPLITUDE_API_KEY);
};

// Keeps Amplitude's user ID and properties in line with the logged-in user.
const trackIdentity = (app: string, initialRouteName?: string | null) => {
  // The route of the page being loaded is known before its first navigation
  // has finished, so the identity can be set before the first events.
  const routeName = ref(initialRouteName);
  router.afterEach((to) => (routeName.value = to.name));

  // The logged-in user's session details, and the client they were loaded for.
  const sessionDetails = ref<{
    clientKey: string;
    userId: number;
    userRole?: string;
    micadUser: boolean;
  }>();

  watchEffect((onInvalidate) => {
    // Visitors who aren't logged in are tracked anonymously by device ID.
    // The login is kept per tab, so a tab without one (a new tab, or the login
    // page opened from another app) leaves the shared identity alone. A tab
    // whose own login expired still has its user ID, and the cookie may still
    // hold that user's ID, so it's cleared; the device ID is kept. Before the
    // SDK has started, getUserId() doesn't know the stored ID yet, so this
    // doesn't check it first.
    if (!isLoggedIn.value) {
      if (auth.userId) {
        amplitude.setUserId(undefined);
        sentProperties = "";
      }
      return;
    }

    // Login and client chooser pages are anonymous routes. The user hasn't
    // chosen a client there yet, and the stored client key may be from an
    // earlier session, so keep the current identity until they're in the app.
    const inApp =
      !!routeName.value && !anonymousRoutes.includes(routeName.value);
    const clientKey = getKV(CLIENT_COOKIE_NAME);
    const userId = auth.userId;
    if (!inApp || !clientKey || !userId) return;

    // Amplitude recommends sending the user ID as soon as the user is known,
    // so events from here on are attributed to them.
    const amplitudeUserId = `${clientKey}:${userId}`;
    if (amplitude.getUserId() !== amplitudeUserId) {
      amplitude.setUserId(amplitudeUserId);
    }

    // The user's role comes with their session details, loaded separately.
    // fetchUserInfo() caches them per session, and the cache may be from
    // before a client switch (the response doesn't say which client it's for),
    // so they're loaded here for each user and client.
    const details = sessionDetails.value;
    if (
      details?.clientKey !== clientKey ||
      String(details.userId) !== String(userId)
    ) {
      let outdated = false;
      onInvalidate(() => (outdated = true));

      getUserSession().then(({ payload: us }) => {
        if (outdated || !us || String(us.userId) !== String(userId)) return;

        // The same role as fetchUserInfo() gives in myRole.
        const userRole = us.micadUser
          ? "MicadAdmin"
          : us.appRoles?.["Accounts"] || "None";

        sessionDetails.value = {
          clientKey,
          userId: us.userId,
          userRole,
          micadUser: us.micadUser,
        };
      });
      return;
    }

    // Built-in properties are listed last so a config key can't override them.
    const userProperties = {
      app,
      clientKey,
      userId: details.userId,
      userRole: details.userRole,
      micadUser: details.micadUser,
    };

    // The user may log out or change before the config request returns;
    // in that case the result must not be applied to the new user.
    let outdated = false;
    onInvalidate(() => (outdated = true));

    loadClientProperties(clientKey).then((properties) => {
      if (!outdated) setUserProperties({ ...properties, ...userProperties });
    });
  });
};

export const init = async (app: string) => {
  // The logout page only clears the identity. Starting the SDK there could
  // write the old identity back to the cookie after logout() has cleared it.
  const routeName = router.resolve(window.location.pathname).route.name;
  if (routeName === "Logout") return;

  // Wait for the config and the saved login before identifying the user.
  if (!(await initApp()) || !config.AMPLITUDE_API_KEY) return;

  // Calls made before the SDK has started are queued and applied in order
  // once it starts, so the first events already have the right identity.
  trackIdentity(app, routeName);

  try {
    await amplitude.initAll(config.AMPLITUDE_API_KEY, {
      analytics: { autocapture: true },
    });
  } catch (err) {
    console.error("Amplitude failed to start", err);
  }
};
